import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { request } from '../axios';
import type { FileDto } from '../generated/model';

export const PatientDocumentCategory = {
  DOT: 'DOT',
  INDEX_PATIENT: 'INDEX_PATIENT',
  SCREENING: 'SCREENING',
  CONTACT_TRACING: 'CONTACT_TRACING',
  OTHERS: 'OTHERS',
} as const;

export type PatientDocumentCategory = (typeof PatientDocumentCategory)[keyof typeof PatientDocumentCategory];

export interface PatientDocumentDto {
  fileId: string;
  category: PatientDocumentCategory;
  fileName: string;
  lastUpdated: string;
}

export interface PersonDocumentDto {
  fileId: string;
  category: PatientDocumentCategory;
  fileName: string;
  lastUpdated: string;
  caseNumber?: string | null;
}

type PatientDocumentApiDto = Omit<PatientDocumentDto, 'category'> & {
  category: DocumentCategory;
};

type PersonDocumentApiDto = Omit<PersonDocumentDto, 'category'> & {
  category: DocumentCategory;
};

export const getPatientDocuments = (patientId: string, signal?: AbortSignal) => {
  return request<PatientDocumentApiDto[]>({
    url: `/api/patients/${patientId}/documents`,
    method: 'GET',
    signal,
  }).then((documents) => documents.map(normalizePatientDocument));
};

export const getGetPatientDocumentsQueryKey = (patientId: string) => {
  return [`/api/patients/${patientId}/documents`] as const;
};

export function useGetPatientDocumentsSuspense(patientId: string) {
  return useSuspenseQuery({
    queryKey: getGetPatientDocumentsQueryKey(patientId),
    queryFn: ({ signal }) => getPatientDocuments(patientId, signal),
  });
}

export const getPersonDocuments = (personId: string, signal?: AbortSignal) => {
  return request<PersonDocumentApiDto[]>({
    url: `/api/persons/${personId}/documents`,
    method: 'GET',
    signal,
  }).then((documents) => documents.map(normalizePersonDocument));
};

export const getGetPersonDocumentsQueryKey = (personId: string) => {
  return [`/api/persons/${personId}/documents`] as const;
};

export function useGetPersonDocumentsSuspense(personId: string) {
  return useSuspenseQuery({
    queryKey: getGetPersonDocumentsQueryKey(personId),
    queryFn: ({ signal }) => getPersonDocuments(personId, signal),
  });
}

export const getDocumentFile = (fileId: string, signal?: AbortSignal) => {
  return request<FileDto>({
    url: `/api/documents/${fileId}`,
    method: 'GET',
    signal,
  });
};

export const deleteDocument = (fileId: string) => {
  return request<void>({
    url: `/api/documents/${fileId}`,
    method: 'DELETE',
  });
};

export function useDeleteDocument(
  options?: UseMutationOptions<void, Error, string>,
): UseMutationResult<void, Error, string> {
  return useMutation({
    mutationFn: (fileId) => deleteDocument(fileId),
    ...options,
  });
}

export const TemplateType = {
  IP_ABSCHLUSSGESPRAECH: 'IP_ABSCHLUSSGESPRAECH',
  IP_ZWISCHENBERICHT: 'IP_ZWISCHENBERICHT',
  DOT_AUFTRAG: 'DOT_AUFTRAG',
  DOT_VERORDNUNG: 'DOT_VERORDNUNG',
  DOT_UEBERWACHUNGSTABELLE: 'DOT_UEBERWACHUNGSTABELLE',
  SP_BESTAETIGUNG_DE: 'SP_BESTAETIGUNG_DE',
  SP_BESTAETIGUNG_EN: 'SP_BESTAETIGUNG_EN',
  SP_BESTAETIGUNG_FR: 'SP_BESTAETIGUNG_FR',
  SP_BESTAETIGUNG_ES: 'SP_BESTAETIGUNG_ES',
  SP_BLUTTEST_NEGATIV: 'SP_BLUTTEST_NEGATIV',
  SP_EINWILLIGUNG_DATENSCHUTZ: 'SP_EINWILLIGUNG_DATENSCHUTZ',
  SP_QUITTUNG: 'SP_QUITTUNG',
  SP_RX_KARTE_BAZ: 'SP_RX_KARTE_BAZ',
  SP_THORAX_ROENTGENKARTE: 'SP_THORAX_ROENTGENKARTE',
  UU_AUFTRAG_UEBERKANTONAL: 'UU_AUFTRAG_UEBERKANTONAL',
  UU_AUFGEBOT_MIT_TERMIN: 'UU_AUFGEBOT_MIT_TERMIN',
  UU_AUFGEBOT_OHNE_TERMIN: 'UU_AUFGEBOT_OHNE_TERMIN',
  UU_BERICHT_HA_MDR: 'UU_BERICHT_HA_MDR',
  UU_BERICHT_HA_STN_TB_TBI: 'UU_BERICHT_HA_STN_TB_TBI',
  UU_BLUTTEST_NEGATIV: 'UU_BLUTTEST_NEGATIV',
  UU_TBI_ABSCHLUSS: 'UU_TBI_ABSCHLUSS',
  UU_TBI_START: 'UU_TBI_START',
  UU_THORAX_ROENTGENKARTE: 'UU_THORAX_ROENTGENKARTE',
  UU_UEBERWEISUNG_KISPI_KSW: 'UU_UEBERWEISUNG_KISPI_KSW',
  UU_UEBERWEISUNG_TBI_HA: 'UU_UEBERWEISUNG_TBI_HA',
  KURZBRIEF: 'KURZBRIEF',
} as const;

export type TemplateType = (typeof TemplateType)[keyof typeof TemplateType];

export interface GenerateDocumentDto {
  patientId: string;
  templateType: TemplateType;
}

export const generateDocument = (data: GenerateDocumentDto) =>
  request<unknown>({
    url: `/api/templates/generate`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  });

export function useGenerateDocument(
  options?: UseMutationOptions<unknown, Error, GenerateDocumentDto>,
): UseMutationResult<unknown, Error, GenerateDocumentDto> {
  return useMutation({
    mutationFn: generateDocument,
    ...options,
  });
}

export const DocumentCategory = {
  DOT: 'DOT',
  INDEX: 'INDEX',
  SCREENING: 'SCREENING',
  CONTACT_TRACING: 'CONTACT_TRACING',
  OTHER: 'OTHER',
} as const;

export type DocumentCategory = (typeof DocumentCategory)[keyof typeof DocumentCategory];

const PATIENT_DOCUMENT_CATEGORY_BY_API_CATEGORY: Record<DocumentCategory, PatientDocumentCategory> = {
  [DocumentCategory.DOT]: PatientDocumentCategory.DOT,
  [DocumentCategory.INDEX]: PatientDocumentCategory.INDEX_PATIENT,
  [DocumentCategory.SCREENING]: PatientDocumentCategory.SCREENING,
  [DocumentCategory.CONTACT_TRACING]: PatientDocumentCategory.CONTACT_TRACING,
  [DocumentCategory.OTHER]: PatientDocumentCategory.OTHERS,
};

function normalizePatientDocument(document: PatientDocumentApiDto): PatientDocumentDto {
  return {
    ...document,
    category: PATIENT_DOCUMENT_CATEGORY_BY_API_CATEGORY[document.category],
  };
}

function normalizePersonDocument(document: PersonDocumentApiDto): PersonDocumentDto {
  return {
    ...document,
    category: PATIENT_DOCUMENT_CATEGORY_BY_API_CATEGORY[document.category],
  };
}

export interface DocumentDto {
  id: string;
  patientId: string;
  personId: string | null;
  personName: string | null;
  category: DocumentCategory;
  displayName: string;
  fileId: string;
  fileName: string;
  source: 'UPLOADED' | 'GENERATED';
  templateType: string | null;
  templateVersion: number | null;
  created: string | null;
  createdBy: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface UploadPatientDocumentDto {
  patientId: string;
  file: File;
  category: PatientDocumentCategory;
}

const UPLOAD_DOCUMENT_CATEGORY: Record<PatientDocumentCategory, DocumentCategory> = {
  [PatientDocumentCategory.DOT]: DocumentCategory.DOT,
  [PatientDocumentCategory.INDEX_PATIENT]: DocumentCategory.INDEX,
  [PatientDocumentCategory.SCREENING]: DocumentCategory.SCREENING,
  [PatientDocumentCategory.CONTACT_TRACING]: DocumentCategory.CONTACT_TRACING,
  [PatientDocumentCategory.OTHERS]: DocumentCategory.OTHER,
};

export const uploadPatientDocument = ({ patientId, file, category }: UploadPatientDocumentDto) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', UPLOAD_DOCUMENT_CATEGORY[category]);

  return request<DocumentDto>({
    url: `/api/patients/${patientId}/documents`,
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
  });
};

export function useUploadPatientDocument(
  options?: UseMutationOptions<DocumentDto, Error, UploadPatientDocumentDto>,
): UseMutationResult<DocumentDto, Error, UploadPatientDocumentDto> {
  return useMutation({
    mutationFn: uploadPatientDocument,
    ...options,
  });
}
